import { useSelector, useDispatch } from "react-redux";
import { apiClient } from "../services/api/apiClient";
import { useEffect, useState } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { refreshProject } from "../redux/actions/projectActions";

export default function Settings() {
    const dispatch = useDispatch();
    const selectedProjectId = useSelector(
        (state) => state.project.selectedProjectId
    );
    const [projectDetails, setProjectDetails] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        job_name: "",
        job_number: "",
        engineer: "",
        architect: "",
        customer: ""
    });

    useEffect(() => {
        //fetch project details
        const fetchProject = async () => {
            if (selectedProjectId) {
                const response = await apiClient.get(`/projects/${selectedProjectId}`);
                setProjectDetails(response.data);
                setFormData({
                    job_name: response.data?.job_name || "",
                    job_number: response.data?.job_number || "",
                    engineer: response.data?.engineer || "",
                    architect: response.data?.architect || "",
                    customer: response.data?.customer || ""
                });
            }
        }
        fetchProject();
    }, [selectedProjectId])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        // Reset form data to original project details
        setFormData({
            job_name: projectDetails?.job_name || "",
            job_number: projectDetails?.job_number || "",
            engineer: projectDetails?.engineer || "",
            architect: projectDetails?.architect || "",
            customer: projectDetails?.customer || ""
        });
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        try {
            await apiClient.put(`/projects/${selectedProjectId}`, formData);
            setProjectDetails({ ...projectDetails, ...formData });
            setIsEditing(false);
            // Dispatch refresh action to update navigation
            dispatch(refreshProject());
        } catch (error) {
            console.error("Failed to update project:", error);
            alert("Failed to save project changes. Please try again.");
        }
    };

    const inputClassName = isEditing
        ? "w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-indigo-500 focus:border-indigo-500"
        : "w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";

    return (
        <div className="p-8 bg-gray-100 w-full h-full min-h-screen">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
                </div>

                {/* Project Information Card */}
                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    {/* Card Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                        <h2 className="text-base font-medium text-gray-700">
                            Project Information
                        </h2>
                        {selectedProjectId && (
                            <div className="flex items-center space-x-3">
                                {!isEditing ? (
                                    <button
                                        type="button"
                                        onClick={handleEditClick}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                                    >
                                        <PencilIcon className="w-4 h-4 mr-2" />
                                        Edit Project
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleCancelClick}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveClick}
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                                        >
                                            Save Changes
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {!selectedProjectId ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500 text-sm">
                                    Please select a project to view and edit its information.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4">
                                {/* Job Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Job Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.job_name}
                                        onChange={(e) => handleInputChange("job_name", e.target.value)}
                                        readOnly={!isEditing}
                                        className={inputClassName}
                                    />
                                </div>

                                {/* Job Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Job Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.job_number}
                                        onChange={(e) => handleInputChange("job_number", e.target.value)}
                                        readOnly={!isEditing}
                                        className={inputClassName}
                                    />
                                </div>

                                {/* Engineer */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Engineer
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.engineer}
                                        onChange={(e) => handleInputChange("engineer", e.target.value)}
                                        readOnly={!isEditing}
                                        className={inputClassName}
                                    />
                                </div>

                                {/* Architect */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Architect
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.architect}
                                        onChange={(e) => handleInputChange("architect", e.target.value)}
                                        readOnly={!isEditing}
                                        className={inputClassName}
                                    />
                                </div>

                                {/* Customer */}
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Customer
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.customer}
                                        onChange={(e) => handleInputChange("customer", e.target.value)}
                                        readOnly={!isEditing}
                                        className={inputClassName}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}